import { useCallback, useEffect, useMemo, useState } from 'react';

const LEVELS = ['TK', 'SD', 'SMP', 'SMA'];

async function apiRequest(apiBase, path, options = {}) {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    throw new Error(payload.message || 'Request failed');
  }
  return payload;
}

function AdmissionsPortal({ apiBase }) {
  const [mode, setMode] = useState('login');
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const [authForm, setAuthForm] = useState({
    full_name: '',
    email: '',
    password: '',
    level: 'SD',
  });

  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [applications, setApplications] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const selectedLevel = useMemo(() => authForm.level || 'SD', [authForm.level]);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest(apiBase, '/admissions/me');
      setSession(res.data?.user || res.data || null);
      setError('');
    } catch (_) {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const loadFields = useCallback(async (level) => {
    try {
      const res = await apiRequest(apiBase, `/admissions/forms/${String(level).toLowerCase()}`);
      setFields(res.data?.fields || []);
    } catch (err) {
      setError(err.message || 'Failed to load form fields');
    }
  }, [apiBase]);

  const loadApplications = useCallback(async () => {
    try {
      const res = await apiRequest(apiBase, '/admissions/applications/mine');
      setApplications(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load applications');
    }
  }, [apiBase]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    loadFields(selectedLevel);
  }, [loadFields, selectedLevel]);

  useEffect(() => {
    if (session) {
      loadApplications();
    }
  }, [session, loadApplications]);

  async function handleAuth(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');

    try {
      if (mode === 'register') {
        await apiRequest(apiBase, '/admissions/register', {
          method: 'POST',
          body: JSON.stringify(authForm),
        });
        setMessage('Registration successful. Please login.');
        setMode('login');
      } else {
        await apiRequest(apiBase, '/admissions/login', {
          method: 'POST',
          body: JSON.stringify({ email: authForm.email, password: authForm.password }),
        });
        await loadSession();
        setMessage('Login successful.');
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitApplication(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');

    try {
      await apiRequest(apiBase, '/admissions/applications', {
        method: 'POST',
        body: JSON.stringify({
          level_code: selectedLevel.toLowerCase(),
          form_payload: fieldValues,
        }),
      });
      setFieldValues({});
      setMessage('Application submitted successfully.');
      await loadApplications();
    } catch (err) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogout() {
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      await apiRequest(apiBase, '/admissions/logout', { method: 'POST' });
      setSession(null);
      setApplications([]);
      setMessage('Logged out successfully.');
    } catch (err) {
      setError(err.message || 'Failed to logout');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <main className="admissions-page">
        <section className="admissions-card">
          <h2>Admissions Portal</h2>
          <p>Loading admissions portal...</p>
        </section>
      </main>
    );
  }

  if (!session) {
    return (
      <main className="admissions-page">
        <section className="admissions-card">
          <h2>Admissions Portal</h2>
          <p>Register to start your application, or login to continue.</p>
          {error && <div className="alert alert-error">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}

          <div className="admissions-mode-switch">
            <button type="button" onClick={() => setMode('login')} className={mode === 'login' ? 'active' : ''}>
              Login
            </button>
            <button type="button" onClick={() => setMode('register')} className={mode === 'register' ? 'active' : ''}>
              Register
            </button>
          </div>

          <form className="admissions-form" onSubmit={handleAuth}>
            {mode === 'register' && (
              <>
                <label>
                  Full Name
                  <input
                    type="text"
                    value={authForm.full_name}
                    onChange={(event) => setAuthForm((prev) => ({ ...prev, full_name: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Admission Level
                  <select
                    value={authForm.level}
                    onChange={(event) => setAuthForm((prev) => ({ ...prev, level: event.target.value }))}
                  >
                    {LEVELS.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </label>
              </>
            )}

            <label>
              Email
              <input
                type="email"
                value={authForm.email}
                onChange={(event) => setAuthForm((prev) => ({ ...prev, email: event.target.value }))}
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={authForm.password}
                onChange={(event) => setAuthForm((prev) => ({ ...prev, password: event.target.value }))}
                required
              />
            </label>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Processing...' : mode === 'register' ? 'Register' : 'Login'}
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="admissions-page">
      <section className="admissions-card">
        <div className="admissions-header-row">
          <h2>Admissions Portal</h2>
          <button type="button" onClick={handleLogout} disabled={submitting}>Logout</button>
        </div>
        <p>
          Logged in as <strong>{session.full_name}</strong> ({session.email})
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <h3>Submit Application Form</h3>
        <form className="admissions-form" onSubmit={handleSubmitApplication}>
          {fields.map((field) => (
            <label key={field.key_name}>
              {field.label || field.key_name}
              <input
                type={field.field_type === 'number' ? 'number' : 'text'}
                value={fieldValues[field.key_name] || ''}
                onChange={(event) => setFieldValues((prev) => ({ ...prev, [field.key_name]: event.target.value }))}
                required={Boolean(field.is_required)}
              />
            </label>
          ))}
          <button type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Application'}</button>
        </form>

        <h3>My Applications</h3>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Level</th>
                <th>Status</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((application) => (
                <tr key={application.id}>
                  <td>{application.id}</td>
                  <td>{application.level_code}</td>
                  <td>{application.status_code}</td>
                  <td>{application.created_at}</td>
                </tr>
              ))}
              {!applications.length && (
                <tr>
                  <td colSpan={4}>No applications yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

export default AdmissionsPortal;
